import { Icon, RadioButton, RadioGroup, Tooltip } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { round } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { unwrappedMetricLookupMapAtom } from '@/atoms/queries/metric';
import { playerRadarTableColumnsAtom, teamRadarTableColumnsAtom } from '@/atoms/queries/tableColumns';
import {
  radarTemplateCurrentMetricAtom,
  radarTemplateLimitModesAtom,
  radarTemplateCustomLimitsAtom,
} from '@/atoms/radar';
import { toRawValue } from '@/components/sliders/sliderUtils';
import { useMetricDistributionAtoms } from '@/hooks/useMetricDistributionAtoms';
import { MinMax } from '@/types/metric';
import { LimitMode } from '@/types/radar';
import { getMetricDistribution, getMetricBucket, getPhysicalMetricPrecision } from '@/utils/metrics';
import { getTranslationMetrics } from '@/utils/translations';
import { MetricLimitsCustomControls } from './MetricLimitsCustomControls';
import { getPercentileLimits, currentFormattedMinMaxSliderValues } from './metricLimitsUtils';

const translations = 'createRadarTemplateModal.step3';
const defaultOptionId = 'default-metric-limits';
const customOptionId = 'custom-metric-limits';

/**
 * Rounds raw min/max values to align with step increments.
 * Converts to display values (scaling percentages), rounds to precision, then converts back to raw values.
 * This ensures stored values are always step-aligned, preventing slider drift.
 */
const roundToStepAlignment = (min: number, max: number, isPercentage: boolean, precision: number): MinMax => {
  const displayMin = isPercentage ? min * 100 : min;
  const displayMax = isPercentage ? max * 100 : max;
  const roundedDisplayMin = round(displayMin, precision);
  const roundedDisplayMax = round(displayMax, precision);
  return {
    min: toRawValue(roundedDisplayMin, isPercentage),
    max: toRawValue(roundedDisplayMax, isPercentage),
  };
};

export const MetricLimitsMainContent = () => {
  const { t } = useTranslation(['modal', 'metrics']);
  const { isPlayerSection, percentiles, buckets } = useMetricDistributionAtoms();
  const activeMetric = useAtomValue(radarTemplateCurrentMetricAtom);
  const [limitModes, setLimitModes] = useAtom(radarTemplateLimitModesAtom);
  const [customLimits, setCustomLimits] = useAtom(radarTemplateCustomLimitsAtom);

  const isPlayerSectionByRoute = !!useMatch('/:locale/player/*');
  const radarColumnsAtom = isPlayerSectionByRoute ? playerRadarTableColumnsAtom : teamRadarTableColumnsAtom;
  const radarColumns = useAtomValue(radarColumnsAtom);
  const metricLookupMap = useAtomValue(unwrappedMetricLookupMapAtom);

  const currentLimitMode = limitModes[activeMetric] || 'default';
  const selectedOption = currentLimitMode === 'custom' ? customOptionId : defaultOptionId;

  // We should always have percentiles and bucket data for the active metric by the time this component renders,
  // fallbacks are just a safeguard against unexpected data loading issues.
  const metricPercentiles = getMetricDistribution(activeMetric, percentiles);
  const metricBuckets = getMetricBucket(activeMetric, buckets);
  const metricQuartiles = metricBuckets.quartiles;

  const isPercentage = metricPercentiles.percentage;
  const isDisabled = currentLimitMode !== 'custom';
  const physicalOverride = getPhysicalMetricPrecision(activeMetric, metricLookupMap, radarColumns);
  const { precision, step } = physicalOverride ?? metricPercentiles;

  const mergeIntoCustomLimits = (minMax: MinMax) =>
    setCustomLimits(existingCustomLimits => ({
      ...existingCustomLimits,
      [activeMetric]: minMax,
    }));

  const handleModeChange = (limitMode: LimitMode) => {
    setLimitModes(prev => ({ ...prev, [activeMetric]: limitMode }));

    // Initialize custom limits with 5th/95th percentile when switching to custom mode
    if (limitMode === 'custom' && metricPercentiles && !customLimits[activeMetric]) {
      const minMax = getPercentileLimits(metricPercentiles);
      const rounded = roundToStepAlignment(minMax.min, minMax.max, isPercentage, precision);
      mergeIntoCustomLimits(rounded);
    }
  };

  /**
   * Handles all custom limit changes from slider drags, NumberInput button clicks, and blur events.
   * Rounds values to step alignment before storing to prevent slider drift.
   */
  const handleCustomLimitsChange = (_e: React.ChangeEvent<HTMLInputElement>, minMaxValues: [number, number]) => {
    // Convert to raw values and round to align with step
    // This ensures atom values are always step-aligned, preventing drift
    const rawMin = toRawValue(minMaxValues[0], isPercentage);
    const rawMax = toRawValue(minMaxValues[1], isPercentage);
    const rounded = roundToStepAlignment(rawMin, rawMax, isPercentage, precision);

    mergeIntoCustomLimits(rounded);
  };

  // Get committed atom values for slider and reference
  const { currentMin: atomMin, currentMax: atomMax } = currentFormattedMinMaxSliderValues(
    activeMetric,
    customLimits,
    metricPercentiles,
    isPercentage,
  );

  return (
    <div className="px-4 pb-4 flex flex-col gap-4">
      <RadioGroup
        groupId="set-metric-limits"
        groupTitle={t(`${getTranslationMetrics(activeMetric)}.name`, { ns: 'metrics' })}
        size="regular"
      >
        <div className="flex items-center gap-2">
          <RadioButton
            checked={selectedOption === defaultOptionId}
            id={defaultOptionId}
            label={t(`${translations}.options.${isPlayerSection ? 'playerDefault' : 'teamDefault'}`)}
            onChange={() => handleModeChange('default')}
            size="small"
            value={defaultOptionId}
          />
          {isPlayerSection && (
            <Tooltip content={t(`${translations}.tooltip`)}>
              <Icon variant="Info" size="small" />
            </Tooltip>
          )}
        </div>
        <RadioButton
          checked={selectedOption === customOptionId}
          id={customOptionId}
          label={t(`${translations}.options.custom`)}
          onChange={() => handleModeChange('custom')}
          size="small"
          value={customOptionId}
        />
      </RadioGroup>

      <MetricLimitsCustomControls
        activeMetric={activeMetric}
        customLimits={customLimits}
        isReversed={metricPercentiles.reverseScale}
        metricBuckets={metricBuckets}
        isDisabled={isDisabled}
        isPlayerSection={isPlayerSection}
        precision={precision}
        step={step}
        atomMin={atomMin}
        atomMax={atomMax}
        isPercentage={isPercentage}
        quartiles={metricQuartiles}
        onLimitsChange={handleCustomLimitsChange}
      />
    </div>
  );
};
